import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { SelectionModel } from '@components/DataTable/types';
import { UPDATE_PROS_FEATURED } from '@gql/pros';
import { usePageSize } from '@hooks/usePageSize';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import CompanyDataTable from './CompanyDataTable';
import CompanyFilter from './CompanyFilter';
import AddCompanyModal from './components/AddCompanyModal';
import { useGetCompanies, CompaniesFilter } from './hooks/useGetCompanies';
import { useGetCompanyAvailableFilters } from './hooks/useGetCompanyAvailableFilters';

const CompanyList = () => {
    const { showSnackbar } = useSnackbar();

    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<CompaniesFilter>({});
    const [page, setPage] = useState(0);
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'companies-table-page-size' });
    const [sortBy, setSortBy] = useState<{ field: string; order: number }>({ field: 'createdDate', order: -1 });
    const [selectionModel, setSelectionModel] = useState<SelectionModel>({
        selectedRows: [],
        isAllSelected: false,
        isSomeSelected: false,
    });
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const { companies, totalDocs, loading, error, loadMore, isLoadingMore, refetch } = useGetCompanies(
        search,
        filter,
        sortBy,
        page,
        pageSize,
    );

    const { availableFilters } = useGetCompanyAvailableFilters(filter, search);

    const [updateProsFeatured, { loading: isUpdatingFeatured }] = useMutation(UPDATE_PROS_FEATURED);

    const selectedIds = selectionModel.selectedRows.map(id => id.toString());

    useEffect(() => {
        setSelectionModel({ selectedRows: [], isAllSelected: false, isSomeSelected: false });
    }, [page, pageSize, search, sortBy, filter]);

    const handleQueryChange = useCallback((value: string) => {
        setSearch(value || '');
        setPage(0);
    }, []);

    const handleFilterChange = useCallback((newFilter: CompaniesFilter) => {
        setFilter(newFilter);
        setPage(0);
    }, []);

    const handleSortModelChange = useCallback((model: { field: string; sort?: 'asc' | 'desc' | null }[]) => {
        const activeSort = model[0];
        if (!activeSort?.sort) {
            setSortBy({ field: 'createdDate', order: -1 });
            setPage(0);
            return;
        }
        setSortBy({ field: activeSort.field, order: activeSort.sort === 'asc' ? 1 : -1 });
        setPage(0);
    }, []);

    const handleBulkFeaturedUpdate = async (isFeatured: boolean) => {
        if (selectedIds.length === 0) {
            showSnackbar('Sélectionnez au moins un établissement.', 'warning');
            return;
        }
        try {
            await updateProsFeatured({ variables: { ids: selectedIds, isFeatured } });
            showSnackbar('Statut "en vedette" mis à jour.', 'success');
            await refetch();
            setSelectionModel({ selectedRows: [], isAllSelected: false, isSomeSelected: false });
        } catch (err: any) {
            showSnackbar(err?.message || 'Échec de la mise à jour du statut en vedette.', 'error');
        }
    };

    if (error) {
        return (
            <Box>
                <Typography color="error.main">Impossible de charger les établissements.</Typography>
            </Box>
        );
    }

    return (
        <Stack
            sx={{
                borderRadius: 3,
                background: 'none',
                overflowX: 'hidden',
                flex: 1,
                display: 'flex',
            }}
        >
            <CompanyFilter
                totalDocs={totalDocs}
                selectedIds={selectedIds}
                isUpdatingFeatured={isUpdatingFeatured}
                filter={filter}
                availableFilters={availableFilters}
                onQueryChange={handleQueryChange}
                onFilterChange={handleFilterChange}
                onBulkFeaturedUpdate={handleBulkFeaturedUpdate}
                onAddButtonClick={() => setIsAddModalOpen(true)}
            />
            <CompanyDataTable
                companies={companies}
                totalDocs={totalDocs}
                loading={loading}
                isLoadingMore={isLoadingMore}
                page={page}
                pageSize={pageSize}
                selectionModel={selectionModel}
                sortModel={[{ field: sortBy.field, sort: sortBy.order === 1 ? 'asc' : 'desc' }]}
                onPageChange={setPage}
                onPageSizeChange={(newSize) => {
                    storePageSize(newSize);
                    setPage(0);
                }}
                onSelectionModelChange={setSelectionModel}
                onSortModelChange={handleSortModelChange}
                onScrollEnd={loadMore}
            />
            <AddCompanyModal
                open={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={() => refetch()}
            />
        </Stack>
    );
};

export default CompanyList;
